import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { InputSummary, InputSummaryItem } from '../../input-summary';
import { FeedstockFinancial } from './feedstock-financial.model';
import { FeedstockQuantities } from './feedstock-quantities.model';
import { FeedstockCharacteristics } from './feedstock-characteristics.model';
import { FeedstockBmpPotential, FeedstockBmpType } from './feedstock-bmp-potential/feedstock-bmp-potential.model';
import { FeedstockNpk } from './feedstock-npk.model';
import { UnitValue } from '../../data-structures/unit-value.model';
import { FeedstockQuantitiesValidation } from '../../../helper/validation/validation-sub-modules/feedstock-validation/feedstock-quantities.validation';
import { FinancialFeedstockValidation } from '../../../helper/validation/validation-sub-modules/financial-validation/financial-feedstock.validation';
import { FeedstockAutofillPackage } from './feedstock-autofill-package.model';
import {v4 as uuid} from 'uuid';
import AnessaListItemUtil, { AnessaListItem } from '../../anessa-list-item.interface';


/**
 * An interface used to define the Feedstock class structure
 */
export interface FeedstockInterface {
  name: string;
  uuid: string;
  description: string;
  materialId: string;
  category: string;
  type: string;
  source: string;
  catTranslationId: string;
  typeTranslationId: string;
  sourceTranslationId: string;
  quantities: FeedstockQuantities;
  characteristics: FeedstockCharacteristics;
  bmpPotential: FeedstockBmpPotential;
  financial: FeedstockFinancial;
  npkEnabled: boolean;
  npk: FeedstockNpk;
  autofillPackage: FeedstockAutofillPackage;
}

/**
 * constructs the Feedstock class.
 * @param param is used to construct the class. If null or not provided, the class will be set to default values
 */
export class Feedstock implements AnessaListItem {
  name: string;
  uuid: string;
  description: string;
  materialId: string;
  category: string;
  type: string;
  source: string;
  catTranslationId: string;
  typeTranslationId: string;
  sourceTranslationId: string;
  quantities: FeedstockQuantities;
  characteristics: FeedstockCharacteristics;
  financial: FeedstockFinancial;
  bmpPotential: FeedstockBmpPotential;
  npkEnabled: boolean;
  npk: FeedstockNpk;
  autofillPackage: FeedstockAutofillPackage;


  /**
   * constructs the Feedstock class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockInterface = {
    name: null,
    uuid: null,
    description: null,
    materialId: null,
    category: null,
    type: null,
    source: null,
    catTranslationId: null,
    typeTranslationId: null,
    sourceTranslationId: null,
    quantities: new FeedstockQuantities(),
    characteristics: new FeedstockCharacteristics(),
    bmpPotential: new FeedstockBmpPotential(),
    financial: new FeedstockFinancial(),
    npkEnabled: false,
    npk: new FeedstockNpk(),
    autofillPackage: new FeedstockAutofillPackage()
  }) {
    this.name = param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.description = param.description;
    this.materialId = param.materialId;
    this.category = param.category;
    this.type = param.type;
    this.source = param.source;
    this.catTranslationId = param.catTranslationId;
    this.typeTranslationId = param.typeTranslationId;
    this.sourceTranslationId = param.sourceTranslationId;
    this.quantities = new FeedstockQuantities(param.quantities);
    this.characteristics = new FeedstockCharacteristics(param.characteristics);
    this.bmpPotential = new FeedstockBmpPotential(param.bmpPotential);
    this.financial = new FeedstockFinancial(param.financial);
    this.npkEnabled = param.npkEnabled;
    this.npk = new FeedstockNpk(param.npk);
    this.autofillPackage = param.autofillPackage;
    this.checkAndUpdateCod();
  }

  /**
   * returns a separate instance of the provided Feedstock obj
   * @param feedstock will be returned as a separate instance
   */
  static deepCopy(feedstock: Feedstock) {
    let copy;
    if (feedstock) {
      copy = new Feedstock(feedstock);
      return copy;
    }
    throw new Error('feedstock copy failed');
  }

  /**
   * Returns a copy of the provided array of feedstocks while maintaining their types.
   * @param feedstocks is an array of Feedstock objects.
   */
  static arrayDeepCopy(feedstocks: Feedstock[]) {
    const copy: Feedstock[] = [];
    if (feedstocks && feedstocks.length) {
      for (const feedstock of feedstocks) {
        copy.push(new Feedstock(feedstock));
      }
    }
    return copy;
  }

  getInputSummary(path: string[], isProjectInput?: boolean) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    // each feedstock will show up in the input summary as one item; for better formatting
    inputSummary.inputs.push(...this.quantities.getSummaryInputs([...path, 'quantities']));
    inputSummary.inputs.push(...this.characteristics.getSummaryInputs([...path, 'characteristics']));
    if (!isProjectInput) {
      inputSummary.inputs = InputSummaryUtils.performPush(inputSummary.inputs, FeedstockPropName.npkEnabled, this.npkEnabled, [...path, 'npkEnabled']);
      if (this.npkEnabled) {
        inputSummary.inputs.push(...this.npk.getSummaryInputs([...path, 'npk']));
      }
    }
    inputSummary.subSection[FeedstockPropName.bmpPotential] = this.bmpPotential.getInputSummary([...path, 'bmpPotential']);
    return inputSummary;
  }

  getFinancialInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = [];
    inputSummary.inputs.push(...this.financial.getSummaryInputs([...path, 'financial']));
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, 'description', this.description, [...path, 'description']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockPropName.category, this.getCatTranslationId(), [...path, 'category']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockPropName.type, this.getTypeTranslationId(), [...path, 'type']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockPropName.source, this.getSourceTranslationId(), [...path, 'source']);
    return inputs;
  }

  /**
   * return true if this feedstocks category, type, and source are selected, false if not
   */
  materialFullySelected(): boolean {
    return !!(this.category && this.type && this.source);
  }

  /**
   * Checks if BMP COD has is the active BMP model type; If it is, the characteristics
   * COD unit value will be set to the BMP COD unit value. These inputs should always be the same,
   * and BMP COD takes priority.
   */
  private checkAndUpdateCod() {
    if (this.characteristics && this.characteristics.cod && this.bmpPotential) {
      if (this.bmpPotential.type === FeedstockBmpType.COD) {
        if (this.bmpPotential.bmpCod && this.bmpPotential.bmpCod.cod) {
          this.bmpPotential.bmpCod.cod = new UnitValue(this.characteristics.cod);
        }
      }
    }
  }

  hasOpexInfo() {
    return new FeedstockQuantitiesValidation(this.quantities).isValid() &&
      new FinancialFeedstockValidation(this.financial).isValid();
  }

  private getCatTranslationId() {
    return this.catTranslationId ? this.catTranslationId : this.category;
  }

  private getTypeTranslationId() {
    return this.typeTranslationId ? this.typeTranslationId : this.type;
  }

  private getSourceTranslationId() {
    return this.sourceTranslationId ? this.sourceTranslationId : this.source;
  }

  getUuidForPath(): string {
    return AnessaListItemUtil.getUuidForPath(this.uuid);
  }
}


export enum FeedstockPropName {
  name = 'INPUT_SUMMARY.FEEDSTOCK.NAME',
  isCustom = 'INPUT_SUMMARY.FEEDSTOCK.IS_CUSTOM',
  category = 'INPUT_SUMMARY.FEEDSTOCK.CATEGORY',
  type = 'INPUT_SUMMARY.FEEDSTOCK.TYPE',
  source = 'INPUT_SUMMARY.FEEDSTOCK.SOURCE',
  quantities = 'INPUT_SUMMARY.FEEDSTOCK.QUANTITIES.TITLE',
  characteristics = 'INPUT_SUMMARY.FEEDSTOCK.CHARACTERISTICS.TITLE',
  financial = 'INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.TITLE',
  bmpPotential = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TITLE',
  npkEnabled = 'INPUT_SUMMARY.FEEDSTOCK.NPK_ENABLED',
  npk = 'INPUT_SUMMARY.FEEDSTOCK.NPK.TITLE',
  id_name = 'name',
  id_isCustom = 'is custom',
  id_category = 'category',
  id_type = 'type',
  id_source = 'source',
  id_quantities = 'quantities',
  id_characteristics = 'characteristics',
  id_financial = 'financial',
  id_bmpPotential = 'biomethane potential',
  id_npkEnabled = 'is npk enabled',
  id_npk = 'npk analysis'
}


